import axios from 'axios';

import type { CmsContentResponse } from './types';

const isProduction = process.env.NEXT_PUBLIC_STAGE === 'PRD';
const isLocal = process.env.NEXT_PUBLIC_STAGE === 'LOC';

const getCmsBaseUrl = () => {
  if (isProduction) return 'https://zep.us/cms';
  if (isLocal) return '/cms'; // Next.js rewrites로 프록시 (CORS 우회)
  return 'https://dev-3.zep.us/cms';
};

const CMS_BASE_URL = getCmsBaseUrl();

/**
 * zep-cms S3 기반 CMS 클라이언트
 * CDN URL: PRD → https://zep.us/cms, LOC → /cms (프록시), 그 외 → https://dev-3.zep.us/cms
 */
const CMS_INSTANCE = axios.create({
  baseURL: CMS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // 비필수 마케팅 콘텐츠 전용 인스턴스. zep.us/cms S3 가 간헐적으로 길게 hang 하는데
  // (WEB-APP-1QJ), 기존 15s 는 너무 길어 hang 한 유저가 30s+(retry 포함) 헛대기했다.
  // 실측상 정상 응답 p99.9 = 3.67s 라 8s 면 정상 트래픽은 거의 안 자르면서
  // hang 은 절반 가까이 빨리 끊고 react-query 재시도로 회수한다(노출률↑·대기시간↓).
  timeout: 8000,
});

/**
 * Response interceptor — isProduction 에 따라 json 또는 jsonDev 를 고른다.
 *
 * 예전엔 여기서 weegloo 절대 URL 을 우리 주소로 치환했다(`replaceWeeglooCdnUrl`).
 * 그 치환은 **파일이 우리 S3 에 있는지 보지 않아서**, 콘텐츠에 새 weegloo URL 이
 * 들어오는 순간 그 이미지가 프로덕션에서 깨졌다(403). 지금은 CMS 콘텐츠의 URL 자체를
 * 새 주소로 재작성해 두었으므로 치환이 필요 없고, 모르는 URL 은 원래 출처로 가서 뜬다.
 */
CMS_INSTANCE.interceptors.response.use(
  response => {
    const data = response.data as CmsContentResponse;

    // isProduction에 따라 json/jsonDev 선택
    const jsonString = isProduction ? data.json : data.jsonDev;

    // json이 없으면 jsonDev로 fallback
    const finalJsonString = jsonString ?? data.jsonDev ?? data.json;

    try {
      response.data = JSON.parse(finalJsonString);
    } catch {
      console.error('[CMS] JSON 파싱 실패');
      throw new Error('CMS 콘텐츠 파싱 실패');
    }

    return response;
  },
  error => {
    console.error('[CMS] API 에러:', error);
    return Promise.reject(error);
  },
);

/**
 * CMS 콘텐츠 조회 fetcher
 * @param params.contentId - 콘텐츠 ID
 * @returns 파싱된 콘텐츠 데이터
 */
export const getCmsContent = async <T = CmsContentResponse>({
  contentId,
}: {
  contentId: string;
}): Promise<T> => {
  const response = await CMS_INSTANCE.get<T>(`/${contentId}`);
  return response.data;
};

export { CMS_INSTANCE };
